import React, { useMemo, useState, useEffect } from 'react'
import styles from './ScheduleModal.module.scss'
import { Modal } from 'components/Modal'
import Button from 'components/Button'
import { Select, TextArea } from 'components/forms'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import cronstrue from 'cronstrue/i18n'

const generateCronExpression = (schedule) => {
	// Use default values if hours or minutes are not provided
	const hours = schedule.hours || '00'
	const minutes = schedule.minutes || '00'

	let cronExpression = `${minutes} ${hours} `

	// Handle day of week or day of month
	if (schedule.dayOfWeek) {
		// Convert from 1-7 (Sunday=1) to 0-6 (Sunday=0) for cronstrue
		let cronDayOfWeek
		if (Array.isArray(schedule.dayOfWeek)) {
			// Handle multiple days
			cronDayOfWeek = schedule.dayOfWeek.map(day => {
				return day === '1' ? '0' : String(parseInt(day, 10) - 1)
			}).join(',')
		} else {
			// Single day
			cronDayOfWeek = schedule.dayOfWeek === '1' ? '0' : String(parseInt(schedule.dayOfWeek, 10) - 1)
		}
		// When using day of week, use ? for day of month
		cronExpression += '? * ' + cronDayOfWeek + ' *'
	} else if (schedule.dayOfMonth) {
		// When using day of month, use ? for day of week
		cronExpression += schedule.dayOfMonth + ' * ? *'
	} else {
		// Default: every day
		cronExpression += '* * ? *'
	}

	// Handle months if specified
	if (schedule.months?.length > 0) {
		const parts = cronExpression.split(' ')
		parts[3] = schedule.months.join(',')
		cronExpression = parts.join(' ')
	}

	return cronExpression
}

const ScheduleModal = ({ visible, onCancel, onSave, initialSchedule }) => {
	const [t, i18n] = useTranslation()
	let currentLocale
	const currentLanguage = i18n.language
	switch (currentLanguage) {
		case 'English': {
			currentLocale = 'de'
			break
		}
		case 'German': {
			currentLocale = 'de'
			break
		}
		case 'French': {
			currentLocale = 'fr'
			break
		}
		case 'Italian': {
			currentLocale = 'it'
			break
		}
		default: {
			currentLocale = 'de'
			break
		}
	}

	const [activeTab, setActiveTab] = useState('startTime')
	const [schedule, setSchedule] = useState(initialSchedule || {
		hours: '21',
		minutes: '00',
		dayOfWeek: null,
		dayOfMonth: null,
		months: []
	})

	// Ensure dayOfWeek is always an array for consistency
	const [selectedDays, setSelectedDays] = useState(() => {
		if (initialSchedule?.dayOfWeek) {
			return Array.isArray(initialSchedule.dayOfWeek)
				? initialSchedule.dayOfWeek
				: [initialSchedule.dayOfWeek]
		}
		return []
	})

	// Update selectedDays when initialSchedule changes (intentional prop->state sync)
	/* eslint-disable react-hooks/set-state-in-effect */
	useEffect(() => {
		if (initialSchedule?.dayOfWeek) {
			const newSelectedDays = Array.isArray(initialSchedule.dayOfWeek)
				? initialSchedule.dayOfWeek
				: [initialSchedule.dayOfWeek]
			setSelectedDays(newSelectedDays)
		} else {
			setSelectedDays([])
		}
	}, [initialSchedule])
	/* eslint-enable react-hooks/set-state-in-effect */

	const hours = Array.from({ length: 24 }, (_, i) => ({
		value: String(i).padStart(2, '0'),
		label: String(i).padStart(2, '0')
	}))

	const minutes = Array.from({ length: 60 }, (_, i) => ({
		value: String(i).padStart(2, '0'),
		label: String(i).padStart(2, '0')
	}))

	const daysOfWeek = useMemo(() => [
		{ value: '1', label: t('common.dates.sunday_short') },
		{ value: '2', label: t('common.dates.monday_short') },
		{ value: '3', label: t('common.dates.tuesday_short') },
		{ value: '4', label: t('common.dates.wednesday_short') },
		{ value: '5', label: t('common.dates.thursday_short') },
		{ value: '6', label: t('common.dates.friday_short') },
		{ value: '7', label: t('common.dates.saturday_short') }
	], [t])

	const daysOfMonth = Array.from({ length: 31 }, (_, i) => ({
		value: String(i + 1),
		label: String(i + 1)
	}))

	const months = useMemo(() => [
		{ value: '1', label: t('common.dates.january') },
		{ value: '2', label: t('common.dates.february') },
		{ value: '3', label: t('common.dates.march') },
		{ value: '4', label: t('common.dates.april') },
		{ value: '5', label: t('common.dates.may') },
		{ value: '6', label: t('common.dates.june') },
		{ value: '7', label: t('common.dates.july') },
		{ value: '8', label: t('common.dates.august') },
		{ value: '9', label: t('common.dates.september') },
		{ value: '10', label: t('common.dates.october') },
		{ value: '11', label: t('common.dates.november') },
		{ value: '12', label: t('common.dates.december') }
	], [t])

	const handleTabClick = (tab) => {
		setActiveTab(tab)
	}


	const handleSave = () => {
		const cronExpression = generateCronExpression(schedule)
		onSave({
			...schedule,
			cronExpression,
			scheduleText: cronstrue.toString(cronExpression, { locale: currentLocale })
		})
	}

	const scheduleText = useMemo(() => {
		const cronExpression = generateCronExpression(schedule)
		try {
			return cronstrue.toString(cronExpression, { locale: currentLocale })
		} catch (error) {
			console.error('Error generating schedule text:', error)
			return ''
		}
	}, [schedule, currentLocale])

	return (
		<Modal
			label={t('task_section.strings.execution_schedule')}
			open={visible}
			width={700}
			unmount={onCancel}
		>
			<div className={styles.scheduleModal}>
				<div className={styles.tabs}>
					<div
						className={classNames(styles.tab, { [styles.active]: activeTab === 'startTime' })}
						onClick={() => handleTabClick('startTime')}
					>
						<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
							<path d='M12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z' fill='black' fillOpacity='0.56'/>
							<path d='M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z' fill='black' fillOpacity='0.56'/>
						</svg>
						{t('task_section.strings.start_time')}
					</div>
					<div
						className={classNames(styles.tab, { [styles.active]: activeTab === 'dayOfWeek' })}
						onClick={() => handleTabClick('dayOfWeek')}
					>
						<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
							<path d='M9 11H7V13H9V11ZM13 11H11V13H13V11ZM17 11H15V13H17V11ZM19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z' fill='black' fillOpacity='0.56'/>
						</svg>
            			{t('task_section.strings.day_of_the_week')}
					</div>
					<div
						className={classNames(styles.tab, { [styles.active]: activeTab === 'dayOfMonth' })}
						onClick={() => handleTabClick('dayOfMonth')}
					>
						<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
							<path d='M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3.01 3.9 3.01 5L3 19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19ZM7 10H12V15H7V10Z' fill='black' fillOpacity='0.56'/>
						</svg>
						{t('task_section.strings.day_of_the_month')}
					</div>
					<div
						className={classNames(styles.tab, { [styles.active]: activeTab === 'month' })}
						onClick={() => handleTabClick('month')}
					>
						<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
							<path d='M17 12H12V17H17V12ZM16 1V3H8V1H6V3H5C3.89 3 3.01 3.9 3.01 5L3 19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3H18V1H16ZM19 19H5V8H19V19Z' fill='black' fillOpacity='0.56'/>
						</svg>
            			{t('common.dates.dates')}
					</div>
				</div>

				<div className={styles.content}>
					{activeTab === 'startTime' && (
						<div className={styles.timeSelectors}>
							<div className={styles.timeField}>
								<Select
									noForm
									value={schedule.hours}
									onChange={({ value }) => {
										setSchedule({ ...schedule, hours: value })
									}}
									options={hours}
									style={{ width: 120 }}
									label={t('common.dates.hours')}
								/>
							</div>
							<div className={styles.timeField}>
								<Select
									noForm
									value={schedule.minutes}
									onChange={({ value }) => setSchedule({ ...schedule, minutes: value })}
									options={minutes}
									style={{ width: 120 }}
									label={t('common.dates.minutes')}
								/>
							</div>
						</div>
					)}

					{activeTab === 'dayOfWeek' && (
						<div className={styles.weekDays}>
							{daysOfWeek.map((day) => (
								<div
									key={day.value}
									className={selectedDays.includes(day.value) ? styles.active : ''}
									onClick={() => {
										const newSelectedDays = selectedDays.includes(day.value)
											? selectedDays.filter(d => d !== day.value)
											: [...selectedDays, day.value]
										setSelectedDays(newSelectedDays)
										setSchedule({
											...schedule,
											dayOfWeek: newSelectedDays.length > 0 ? newSelectedDays : null,
											dayOfMonth: null
										})
									}}
								>
									{day.label}
								</div>
							))}
						</div>
					)}

					{activeTab === 'dayOfMonth' && (
						<div className={styles.monthDays}>
							{daysOfMonth.map((day) => (
								<div
									key={day.value}
									className={schedule.dayOfMonth === day.value ? styles.active : ''}
									onClick={() => {
										setSchedule({
											...schedule,
											dayOfMonth: day.value,
											dayOfWeek: null
										})
									}}
								>
									{day.label}
								</div>
							))}
						</div>
					)}

					{activeTab === 'month' && (
						<div className={styles.months}>
							{months.map((month) => (
								<div
									key={month.value}
									className={schedule.months?.includes(month.value) ? styles.active : ''}
									onClick={() => {
										const newMonths = schedule.months?.includes(month.value)
											? schedule.months.filter((m) => m !== month.value)
											: [...(schedule.months || []), month.value]
										setSchedule({ ...schedule, months: newMonths })
									}}
								>
									{month.label}
								</div>
							))}
						</div>
					)}

					<div className={styles.schedulePreview}>
						<TextArea
							value={scheduleText}
							disabled
							label={t('task_section.strings.schedule')}
						/>
					</div>
				</div>
			</div>
			<div className={styles.footer}>
				<Button key='cancel' status='transparent' onClick={onCancel} text={t('common.buttons.cancel')} />
				<Button key='save' type='primary' onClick={handleSave} text={t('common.buttons.save')} />
			</div>
		</Modal>
	)
}

export default ScheduleModal
